/**
 * Layout customization for the application form.
 *
 * The site configuration endpoint may return a `layout` object with the same
 * shape as LAYOUT_DEFAULTS; `resolveLayout` merges it over these values.
 */

// Matches Mantine's `Container size="sm"` (45rem), the width used before the layout became configurable.
const DEFAULT_FORM_MAX_WIDTH = 720;

export const LAYOUT_DEFAULTS = {
  form: {
    maxWidth: DEFAULT_FORM_MAX_WIDTH, // px
    paperPadding: 'xl',
    paperRadius: 'md',
    paperShadow: 'sm',
    withBorder: true,
    withShadow: true,
  },
  colors: {
    // `undefined` keeps the color inherited from the Mantine theme.
    pageBackground: undefined,
    paperBackground: undefined,
  },
  header: {
    // Absolute URL of the tenant site embed route, e.g.
    // "https://francinamgmt.com/embed/header". `undefined` renders no header,
    // which is the behaviour before this feature.
    url: undefined,
    // Pixels reserved before the embed reports its own height. `0` reserves
    // nothing; setting the real header height removes the shift.
    initialHeight: 0,
    // Milliseconds to wait for the first message before giving up on the header.
    timeout: 8000,
    // Hides the site configuration logo inside the form card while the header
    // is shown, so the same logo is not drawn twice. Only applies with `url` set.
    hideFormLogo: true,
  },
  typography: {
    // Name of a font declared in `GOOGLE_FONTS`. `undefined` keeps the theme font.
    fontFamily: undefined,
  },
  fields: {
    size: 'md',
    radius: 'md',
    variant: 'default',
    borderSides: 'all', // 'all' | 'bottom'
    gap: 'md', // Space between the form rows
    // Number of columns, or a Mantine breakpoint map such as { base: 1, sm: 2 }.
    // A map replaces this value entirely, so it should carry its own `base`.
    columns: 1,
    // How a single-choice field otherwise drawn as a segmented control is rendered.
    segmentedVariant: 'segmented', // 'segmented' | 'dropdown'
    // 'plain' drops the URL prefix box of the social fields, keeping the URL cleanup.
    socialVariant: 'prefixed', // 'prefixed' | 'plain'
    // 'plain' hides the country selector of the phone field, keeping the number format.
    phoneVariant: 'prefixed', // 'prefixed' | 'plain'
    // 'plain' drops the background and border around the privacy consent.
    privacyVariant: 'box', // 'box' | 'plain'
    // `undefined` keeps the color coming from the Mantine variant.
    background: undefined,
    borderColor: undefined,
    focusBorderColor: undefined,
    textColor: undefined,
    placeholderColor: undefined,
    // `undefined` keeps the label styling of mantine-overrides.css.
    labelSize: undefined,
    labelWeight: undefined,
    labelColor: undefined,
    labelCasing: 'capitalize', // 'capitalize' | 'uppercase' | 'lowercase'
  },
  conversions: {
    metricsPosition: 'beside', // 'beside' | 'below'
    metricsVariant: 'chip', // 'chip' | 'text'
  },
  photos: {
    // Same as `fields.columns`.
    columns: { base: 2, sm: 3, md: 4 },
    gap: 'lg',
  },
};

// JSON has no `undefined`, so a property the backend leaves unset arrives as `null`.
// Dropping it keeps the default instead of pushing `null` down to Mantine, which
// throws when it parses a color.
const withoutNullValues = (group) =>
  Object.fromEntries(Object.entries(group || {}).filter(([, value]) => value !== null));

/**
 * Merges the `layout` override coming from the site configuration over the defaults.
 * Each group is merged on its own, so a partial override keeps the other groups
 * intact and unknown groups sent by the backend are ignored.
 *
 * @param {object} [override] - The `layout` object from the site configuration
 * @returns {typeof LAYOUT_DEFAULTS}
 */
export const resolveLayout = (override) =>
  Object.fromEntries(
    Object.entries(LAYOUT_DEFAULTS).map(([group, defaults]) => [
      group,
      { ...defaults, ...withoutNullValues(override?.[group]) },
    ])
  );
